import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { OrderControllerService } from '../../../../services/order/order-controller.service';
import { OrderShipment } from './../../../../models';
import { LegendItem } from '../../models/legend-item';
import { OrderUtilityService } from '../../../../services';
import { OrderShipmentCount } from '../../models/order-shipment-count';
import { RouterLink } from '@angular/router';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { LegendItemComponent } from '../legend-item/legend-item.component';
import { BarChartModule } from '@swimlane/ngx-charts';
import { OrderShipmentGroupsList } from '../../../../models/order-shipment-groups-list';

@Component({
  selector: 'insight-upcoming-orders',
  templateUrl: './upcoming-orders.component.html',
  styleUrls: ['./upcoming-orders.component.scss', '../../scss/operational-dashboard-shared.scss'],
  imports: [RouterLink, LoadingSpinnerComponent, LegendItemComponent, BarChartModule]
})
export class UpcomingOrdersComponent implements OnInit, OnDestroy {
  public daysOutToShow = 14;
  public didError = false;
  public isLoading = true;
  public allOrderData: OrderShipment[] = [];

  private $unsubscribeSubject = new Subject();

  // all the chart data
  public allChartData = [];
  // the chart data only showing the legend items the user has checked
  public filteredChartData = [];

  // labels
  public xAxisLabel: string = 'Day';
  public yAxisLabel: string = 'Orders';

  // legend items to show/hide by checkboxes
  public legendItems: LegendItem[] = [];

  // chart colors
  public chartColors = { name: '', selectable: true, group: 'quantile', domain: [] };

  constructor(private orderControllerService: OrderControllerService,
    private orderUtilityService: OrderUtilityService) { }

  ngOnInit() {
    this.isLoading = true;
    this.legendItems = this.orderUtilityService.getOrderLegendItems();

    // re-process data every time we get new stuff
    this.orderControllerService.orderDataRefreshedEvent
      .pipe(takeUntil(this.$unsubscribeSubject))
      .subscribe(() => {
        if (this.isLoading || this.orderControllerService.orderDataNeedsRefresh()) {
          const orderShipmentGroupsList: OrderShipmentGroupsList = this.orderControllerService.getAllOrderShipments();

          // remove all the grouping that are done with statuses so we just can work with orders
          this.allOrderData = [];
          for (const group of orderShipmentGroupsList.orderShipmentGroups) {
            this.allOrderData.push(...group.orderShipments);
          }

          // get rid of orders too far in the future that won't appear in graph
          // this should slightly speed up calculations
          const howManyDaysDoWeGraph = this.generateDayRelativeToNow(this.daysOutToShow);
          this.allOrderData = this.allOrderData.filter(ord => ord.shipDate.getTime() < howManyDaysDoWeGraph.getTime());

          // get rid of the time to make calculations more black and white
          this.allOrderData.forEach(order => {
            order.shipDate.setHours(0, 0, 0, 0);
            order.needDate.setHours(0, 0, 0, 0);
            order.orderEntryDate.setHours(0, 0, 0, 0);
          });

          // transforms all the data to what the chart wants
          this.populateChartData(this.allOrderData);

          this.isLoading = false;
        }
      });
  }

  ngOnDestroy() {
    this.$unsubscribeSubject.next();
    this.$unsubscribeSubject.complete();
  }

  private populateChartData(orders: OrderShipment[]): void {
    const updatedChartData = [];

    // calculations for finding past due orders
    const pastDueOrders = orders.filter(order => order.shipDate.getTime() < this.generateDayRelativeToNow(-1).getTime());
    const pastDueChartEntry = this.createChartDataForDay(pastDueOrders, 'Past Due');
    updatedChartData.push(pastDueChartEntry);

    // calculations for max days shown on chart
    for (let i = 0; i < this.daysOutToShow; i++) {
      const ordersForTheDay = orders.filter(order => order.shipDate.getTime() === this.generateDayRelativeToNow(i).getTime());
      // add the chart data with the associated date label
      const dateToAdd = this.generateDayRelativeToNow(i);
      const dayChartData = this.createChartDataForDay(ordersForTheDay, `${dateToAdd.getMonth() + 1}/${dateToAdd.getDate()}`);
      updatedChartData.push(dayChartData);
    }

    this.allChartData = updatedChartData;
    this.resetShownItems();
    this.updateFilteredChartData();
  }

  private resetShownItems() {
    this.legendItems.forEach((item) => item.visible = true);
  }

  private createChartDataForDay(orders: OrderShipment[], dayLabel: string): any {
    const orderCounts = new OrderShipmentCount(orders);

    const entryForDay = {
      'name': dayLabel,
      'series': [
        { 'name': 'ADEBS - CVD', 'value': orderCounts.adebsCVD },
        { 'name': 'ADEBS - HDD', 'value': orderCounts.adebsHardDrive },
        { 'name': 'ADEBS - Electronic', 'value': orderCounts.adebsElectronic },
        { 'name': 'Streaming - Electronic', 'value': orderCounts.streamingElectronic },
        { 'name': 'Streaming - HDD', 'value': orderCounts.streamingHardDrive },
        { 'name': 'Cinelink - Electronic', 'value': orderCounts.cinelinkElectronic },
        { 'name': 'Cinelink - HDD', 'value': orderCounts.cinelinkHardDrive }
      ]
    };

    return entryForDay;
  }

  private generateDayRelativeToNow(amount: number): Date {
    const date = new Date();
    date.setDate(date.getDate() + amount);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  public updateFilteredChartData() {
    this.filteredChartData = [];
    this.allChartData.forEach((dailyData) => {
      let filteredDailyData = dailyData.series
        .filter((series) => this.legendItems.find((item) => item.name == series.name).visible) // find where visible
        .map((series) => ({ 'name': series.name, 'value': series.value })); // make a new item from it

      this.filteredChartData.push({ 'name': dailyData.name, 'series': filteredDailyData });
    });

    // remove colors for items that are hidden so that the colors stay consistent for legend items as they're hidden/shown
    this.chartColors.domain = this.legendItems.filter(legendItem => legendItem.visible).map(legendItem => legendItem.color);
  }
}
